import ScreenWrapper from "@/components/ScreenWrapper"
import {
  createFileRoute,
  useNavigate,
  useRouter,
  useSearch,
} from "@tanstack/react-router"
import { getFilteredProjects } from "@/api/services/project"
import {
  formatProjectDuration,
  TABLE_PAGE_SIZE,
  usePaginatedTableWithUrlState,
  DataTable,
  selectColumn,
  createSortableColumn,
  createCustomColumn,
  createTableSearchSchema,
  projectTypeMap,
  projectTargetGroupMap,
  AbbreviateTableColumn,
} from "toptaal-shared"
import type { ColumnDef } from "@tanstack/react-table"
import type { Project } from "@/api/types/project"
import { projectQueryKeys } from "@/api/query-keys/projects"
import { useOrganizationSuspenseQuery } from "@/api/queries/useOrganizationSuspenseQuery"

export const Route = createFileRoute("/beheer/contracten/")({
  component: RouteComponent,
  validateSearch: createTableSearchSchema(),
})

function RouteComponent() {
  const { data: organization } = useOrganizationSuspenseQuery()
  const searchParams = useSearch({ from: "/beheer/contracten/" })
  const navigate = useNavigate({
    from: "/beheer/contracten/",
  })

  const router = useRouter()

  const {
    isFetching,
    data,
    search,
    setSearch,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    pagination,
    columnVisibility,
    setColumnVisibility,
  } = usePaginatedTableWithUrlState({
    queryKey: projectQueryKeys.all,
    queryFn: getFilteredProjects,
    pageSize: TABLE_PAGE_SIZE,
    searchParams,
    navigate,
    storageKey: "/beheer/contracten",
  })

  const columns: ColumnDef<Project>[] = [
    selectColumn(),
    createSortableColumn({ accessorKey: "id", name: "ID" }),
    createSortableColumn({ accessorKey: "name", name: "Naam" }),
    createCustomColumn({
      id: "duration",
      name: "Looptijd",
      enableSorting: false,
      cell: ({ row }) => {
        const formattedProjectDuration = formatProjectDuration(
          row.original.startDate,
          row.original.endDate,
        )

        return (
          <div className="flex items-center">
            <div className="max-w-[300px] overflow-hidden text-ellipsis whitespace-nowrap">
              {formattedProjectDuration}
            </div>
          </div>
        )
      },
      accessorKey: "startDate",
    }),
    createCustomColumn({
      id: "type",
      name: "Type",
      cell: ({ row }) => {
        return <p>{projectTypeMap[row.original.type]}</p>
      },
      accessorKey: "type",
    }),
    createCustomColumn({
      id: "targetGroup",
      name: "Doelgroep",
      cell: ({ row }) => {
        const entities = row.original.targetGroup
          .map((tg) => projectTargetGroupMap[tg])
          .filter(Boolean)
          .map((label) => ({ name: label as string }))

        return <AbbreviateTableColumn entities={entities} />
      },
      accessorKey: "targetGroup",
    }),
  ]

  const handleNavigateToProjectDetail = (contractId: Project["id"]) => {
    router.navigate({
      to: `/beheer/contracten/$contractId/organisaties/${organization.id}`,
      params: { contractId: contractId.toString() },
    })
  }

  return (
    <ScreenWrapper showManagementMenu>
      <DataTable
        title="Contracten"
        data={data?.data.projects || []}
        isLoading={isFetching}
        pagination={pagination}
        sortDirection={sortDirection}
        sortField={sortField}
        search={search}
        setSearch={setSearch}
        setSortField={setSortField}
        setSortDirection={setSortDirection}
        columns={columns}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        onRowClick={(project) => handleNavigateToProjectDetail(project)}
      />
    </ScreenWrapper>
  )
}
